import { useEffect, useState } from 'react';
import { ScrollText, ShieldCheck, X, MapPin, Loader2, FileSignature, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { compliance } from '../lib/api';
import ImageUploader from './ImageUploader';

/**
 * Compliance panel for merchants.
 *
 * Two blocks:
 *   1) Merchant ToS gate — mandatory digital signature. Blocks the Vault
 *      until signed.
 *   2) Merchant Vault — legal name, Aadhaar photo, DL number+photo+expiry,
 *      shopfront photo, GPS coordinates.
 */
export default function MerchantComplianceTab({ store, onSaved }) {
  const [tos, setTos] = useState({ text: '', version: '', status: null });
  const [signOpen, setSignOpen] = useState(false);
  const [signName, setSignName] = useState('');
  const [signing, setSigning] = useState(false);

  const [vault, setVault] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [t, s, v] = await Promise.all([
      compliance.getTos().catch(() => ({})),
      compliance.tosStatus().catch(() => ({})),
      store?.id ? compliance.getVault(store.id).catch(() => ({ vault: null })) : Promise.resolve({ vault: null }),
    ]);
    setTos({ text: t.text || '', version: t.version || '', status: s });
    setVault(v.vault || {
      legal_name: '', aadhaar_photo_url: '', aadhaar_last4: '',
      drug_license_number: store?.drug_license_number || '',
      drug_license_photo_url: store?.license_photo_url || '',
      drug_license_expiry: '',
      shopfront_photo_url: '',
      gps_lat: store?.lat, gps_lng: store?.lng,
      address: store?.address || '',
    });
  };
  useEffect(() => { load(); }, [store?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const signNow = async () => {
    if (signName.trim().length < 2) { toast.error('Please type your full legal name'); return; }
    setSigning(true);
    try {
      await compliance.signTos({ accepted: true, signature_name: signName.trim() });
      toast.success('Merchant Terms signed');
      setSignOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not sign');
    } finally { setSigning(false); }
  };

  const pickGps = () => {
    if (!navigator.geolocation) { toast.error('GPS not available on this device'); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => setVault({ ...vault, gps_lat: p.coords.latitude, gps_lng: p.coords.longitude }),
      () => toast.error('Could not read GPS'),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const saveVault = async () => {
    if (!vault?.legal_name || vault.legal_name.trim().length < 2) {
      toast.error('Legal name is required'); return;
    }
    setSaving(true);
    try {
      // Strip empty strings — Pydantic pattern regex rejects "" on aadhaar_last4
      const payload = {};
      Object.entries(vault).forEach(([k, v]) => {
        if (v === '' || v === undefined) return;
        payload[k] = v;
      });
      await compliance.saveVault(store.id, payload);
      toast.success('Merchant Vault saved');
      onSaved?.();
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const msg = typeof raw === 'string'
        ? raw
        : Array.isArray(raw)
          ? raw.map((x) => x.msg || JSON.stringify(x)).join(' · ')
          : 'Could not save';
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const signed = tos.status?.signed && tos.status?.up_to_date;

  return (
    <div data-testid="merchant-compliance-tab">
      {/* --- ToS card --- */}
      <section className="card-soft p-5 mb-5 border-2 border-ink/10">
        <div className="flex items-center gap-2 mb-2">
          <ScrollText className="w-4 h-4 text-[#0D3F12]"/>
          <div className="font-heading font-black text-lg">Merchant Terms of Service</div>
          {signed && (
            <span data-testid="tos-signed-pill" className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#E8F5E9] border border-[#A5D6A7] text-[#1B5E20] px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider">
              <CheckCircle2 className="w-3 h-3"/>Signed · v{tos.status?.version}
            </span>
          )}
        </div>
        {!signed ? (
          <>
            <p className="text-sm text-ink/70">
              Every shop owner must digitally sign the Merchant Terms before listing a single pill. The terms confirm you accept <b>100% liability</b> for the quality and authenticity of the medicines you sell, and that all disputes are subject to the <b>exclusive jurisdiction of the Hyderabad Courts</b>.
            </p>
            <div className="mt-3 rounded-lg bg-muted p-3 text-xs text-ink/80 max-h-40 overflow-y-auto whitespace-pre-wrap font-mono">
              {tos.text}
            </div>
            <button
              data-testid="open-tos-sign"
              onClick={() => setSignOpen(true)}
              className="mt-3 btn-primary inline-flex items-center gap-2"
            >
              <FileSignature className="w-4 h-4"/>Read & Sign Terms
            </button>
          </>
        ) : (
          <p className="text-xs text-ink/60">
            Signed on {new Date(tos.status?.signed_at).toLocaleString()} — current version v{tos.status?.version}.
          </p>
        )}
      </section>

      {/* --- Vault card --- */}
      <section className="card-soft p-5 mb-5 border-2 border-ink/10">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-[#1B5E20]"/>
          <div className="font-heading font-black text-lg">Merchant Vault (legal identity)</div>
        </div>
        {!signed && store?.category === 'medical' ? (
          <div className="rounded-lg bg-[#FFF3E0] border border-[#FFCC80] p-3 text-xs text-[#8D3A0A]">
            Please sign the Merchant Terms of Service above before saving the Vault. This is required for medical shops.
          </div>
        ) : (
          <>
            <p className="text-xs text-ink/60 mb-3">
              Your legal identity is stored in an immutable audit trail. It is shown to law enforcement / courts on demand — never to customers.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <label className="block">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-black text-ink mb-1">Legal name (as on Aadhaar)</div>
                  <input
                    data-testid="vault-legal-name"
                    value={vault?.legal_name || ''}
                    onChange={(e) => setVault({ ...vault, legal_name: e.target.value })}
                    className="w-full rounded-lg border-2 border-ink/20 focus:border-ink bg-white px-3 py-2 text-sm outline-none"
                    placeholder="Full legal name"
                  />
                </label>
                <label className="block">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-black text-ink mb-1">Aadhaar (last 4 digits only)</div>
                  <input
                    data-testid="vault-aadhaar-last4"
                    value={vault?.aadhaar_last4 || ''}
                    onChange={(e) => setVault({ ...vault, aadhaar_last4: e.target.value.replace(/\D+/g, '').slice(0, 4) })}
                    className="w-full rounded-lg border-2 border-ink/20 focus:border-ink bg-white px-3 py-2 text-sm outline-none font-mono"
                    placeholder="1234"
                    maxLength={4}
                  />
                </label>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-black text-ink mb-1">Aadhaar photo (front)</div>
                  <ImageUploader testId="vault-aadhaar-uploader" label="aadhaar" value={vault?.aadhaar_photo_url || ''} onChange={(url) => setVault({ ...vault, aadhaar_photo_url: url })}/>
                </div>
              </div>
              <div className="space-y-3">
                <label className="block">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-black text-ink mb-1">Drug Licence number</div>
                  <input
                    data-testid="vault-dl-number"
                    value={vault?.drug_license_number || ''}
                    onChange={(e) => setVault({ ...vault, drug_license_number: e.target.value })}
                    className="w-full rounded-lg border-2 border-ink/20 focus:border-ink bg-white px-3 py-2 text-sm outline-none font-mono"
                    placeholder="e.g. DL-UP-B-24567"
                  />
                </label>
                <label className="block">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-black text-ink mb-1">DL expiry</div>
                  <input
                    data-testid="vault-dl-expiry"
                    type="date"
                    value={(vault?.drug_license_expiry || '').slice(0, 10)}
                    onChange={(e) => setVault({ ...vault, drug_license_expiry: e.target.value })}
                    className="w-full rounded-lg border-2 border-ink/20 focus:border-ink bg-white px-3 py-2 text-sm outline-none"
                  />
                </label>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-black text-ink mb-1">Drug Licence photo</div>
                  <ImageUploader testId="vault-dl-uploader" label="drug-licence" value={vault?.drug_license_photo_url || ''} onChange={(url) => setVault({ ...vault, drug_license_photo_url: url })}/>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-black text-ink mb-1">Shop-front photo</div>
                <ImageUploader testId="vault-shopfront-uploader" label="shopfront" value={vault?.shopfront_photo_url || ''} onChange={(url) => setVault({ ...vault, shopfront_photo_url: url })}/>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-black text-ink mb-1">Physical shop GPS</div>
                <div className="flex gap-2 items-center">
                  <input
                    data-testid="vault-gps-lat"
                    value={vault?.gps_lat ?? ''}
                    onChange={(e) => setVault({ ...vault, gps_lat: parseFloat(e.target.value) || null })}
                    className="w-1/2 rounded-lg border-2 border-ink/20 bg-white px-3 py-2 text-sm outline-none font-mono"
                    placeholder="Latitude"
                  />
                  <input
                    data-testid="vault-gps-lng"
                    value={vault?.gps_lng ?? ''}
                    onChange={(e) => setVault({ ...vault, gps_lng: parseFloat(e.target.value) || null })}
                    className="w-1/2 rounded-lg border-2 border-ink/20 bg-white px-3 py-2 text-sm outline-none font-mono"
                    placeholder="Longitude"
                  />
                </div>
                <button
                  data-testid="vault-gps-autofill"
                  onClick={pickGps}
                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider text-[#0D47A1] hover:underline"
                >
                  <MapPin className="w-3 h-3"/>Use my current location
                </button>
                <textarea
                  data-testid="vault-address"
                  value={vault?.address || ''}
                  onChange={(e) => setVault({ ...vault, address: e.target.value })}
                  className="mt-3 w-full rounded-lg border-2 border-ink/20 bg-white px-3 py-2 text-sm outline-none"
                  rows={2}
                  placeholder="Full physical address"
                />
              </div>
            </div>
            <button
              data-testid="save-vault"
              onClick={saveVault}
              disabled={saving || (store?.category === 'medical' && !signed)}
              className="mt-4 btn-primary disabled:opacity-50 inline-flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin"/>}
              Save Merchant Vault
            </button>
          </>
        )}
      </section>

      {signOpen && (
        <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4" onClick={() => setSignOpen(false)}>
          <div data-testid="tos-sign-dialog" className="bg-white w-full max-w-2xl rounded-2xl p-6 shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSignOpen(false)} className="absolute top-3 right-3 text-ink/40 hover:text-ink" aria-label="Close">
              <X className="w-5 h-5"/>
            </button>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-black text-[#0D3F12]">
              <FileSignature className="w-4 h-4"/>Merchant Terms · v{tos.version}
            </div>
            <h3 className="font-heading font-black text-2xl mt-1">Sign Merchant Terms of Service</h3>
            <div className="mt-3 rounded-lg bg-muted p-4 text-xs text-ink whitespace-pre-wrap max-h-64 overflow-y-auto font-mono">
              {tos.text}
            </div>
            <label className="block mt-4">
              <div className="text-[10px] uppercase tracking-[0.18em] font-black text-ink mb-1">
                Type your full legal name to sign
              </div>
              <input
                data-testid="tos-sign-name"
                value={signName}
                onChange={(e) => setSignName(e.target.value)}
                placeholder="Your full legal name"
                className="w-full rounded-lg border-2 border-ink focus:border-[#0D3F12] bg-white px-3 py-2.5 text-base outline-none"
              />
            </label>
            <button
              data-testid="tos-sign-submit"
              onClick={signNow}
              disabled={signing || signName.trim().length < 2}
              className="mt-4 w-full btn-primary disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {signing && <Loader2 className="w-4 h-4 animate-spin"/>}I agree — sign now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
